import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, TrendingUp, TrendingDown, Clock, CheckCircle, XCircle, RefreshCw, Filter } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import CryptoIcon from '../components/CryptoIcon';
import {
  fetchDepositHistory,
  fetchWithdrawalHistory,
  fetchCombinedHistory,
  fetchSummary,
  getStatusColor,
  getTypeColor,
  formatDate,
  formatAmount,
  getStatusIcon
} from '../services/depositWithdrawalService';

const TransactionHistory = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'deposits', 'withdrawals'
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'pending', 'completed', 'rejected'
  const [history, setHistory] = useState([]);
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, [activeTab, statusFilter]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      // Fetch summary
      const summaryData = await fetchSummary();
      setSummary(summaryData);

      // Fetch history based on active tab
      let historyData;
      const status = statusFilter === 'all' ? null : statusFilter;

      if (activeTab === 'all') {
        const response = await fetchCombinedHistory('all', status);
        historyData = response.history || [];
      } else if (activeTab === 'deposits') {
        const response = await fetchDepositHistory(status);
        historyData = (response.deposits || []).map(d => ({ ...d, type: 'deposit', id: d.deposit_id }));
      } else if (activeTab === 'withdrawals') {
        const response = await fetchWithdrawalHistory(status);
        historyData = (response.withdrawals || []).map(w => ({ ...w, type: 'withdrawal', id: w.withdrawal_id }));
      }

      setHistory(historyData);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load transaction history',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusIconComponent = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-400" />;
      case 'rejected':
        return <XCircle className="w-5 h-5 text-red-400" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 text-white p-4 pb-24">
      {/* Header */}
      <div className="flex items-center mb-6">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-white/10 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <h1 className="text-2xl font-bold ml-4">Transaction History</h1>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-xl p-4 border border-cyan-500/30">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5 text-cyan-400" />
              <span className="text-sm text-gray-400">Total Deposits</span>
            </div>
            <div className="text-2xl font-bold text-cyan-400">
              {summary.deposits?.completed || 0}
            </div>
            <div className="text-xs text-gray-400 mt-1">
              ${summary.deposits?.total_amount?.toFixed(2) || '0.00'}
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-xl p-4 border border-purple-500/30">
            <div className="flex items-center gap-2 mb-2">
              <TrendingDown className="w-5 h-5 text-purple-400" />
              <span className="text-sm text-gray-400">Total Withdrawals</span>
            </div>
            <div className="text-2xl font-bold text-purple-400">
              {summary.withdrawals?.completed || 0}
            </div>
            <div className="text-xs text-gray-400 mt-1">
              ${summary.withdrawals?.total_amount?.toFixed(2) || '0.00'}
            </div>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-4 overflow-x-auto">
        {['all', 'deposits', 'withdrawals'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all ${
              activeTab === tab
                ? 'bg-cyan-500 text-white'
                : 'bg-white/10 text-gray-400 hover:bg-white/20'
            }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Status Filter */}
      <div className="flex gap-2 mb-4 overflow-x-auto">
        {['all', 'pending', 'completed', 'rejected'].map((status) => (
          <button
            key={status}
            onClick={() => setStatusFilter(status)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
              statusFilter === status
                ? 'bg-blue-500 text-white'
                : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>

      {/* Refresh Button */}
      <div className="flex justify-end mb-4">
        <button
          onClick={fetchData}
          disabled={isLoading}
          className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          <span className="text-sm">Refresh</span>
        </button>
      </div>

      {/* Transaction List */}
      <div className="space-y-3">
        {isLoading ? (
          <div className="text-center py-12 text-gray-400">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2" />
            <p>Loading transactions...</p>
          </div>
        ) : history.length === 0 ? (
          <div className="text-center py-12 bg-white/5 rounded-xl border border-white/10">
            <Filter className="w-12 h-12 text-gray-500 mx-auto mb-3" />
            <p className="text-gray-400">No transactions found</p>
            <p className="text-sm text-gray-500 mt-1">
              {statusFilter !== 'all' ? 'Try changing the filter' : 'Start by making a deposit or withdrawal'}
            </p>
          </div>
        ) : (
          history.map((transaction) => (
            <div
              key={transaction.id}
              className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10 hover:border-cyan-500/50 transition-all"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center">
                    <CryptoIcon symbol={transaction.currency?.toUpperCase()} size={24} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">
                        {formatAmount(transaction.amount, transaction.currency)} {transaction.currency?.toUpperCase()}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${getTypeColor(transaction.type)}`}>
                        {transaction.type?.toUpperCase()}
                      </span>
                    </div>
                    <div className="text-sm text-gray-400 mt-0.5">
                      {transaction.network && `Network: ${transaction.network}`}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col items-end gap-1">
                  <div className="flex items-center gap-1">
                    {getStatusIconComponent(transaction.status)}
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${getStatusColor(transaction.status)}`}>
                      {transaction.status?.toUpperCase()}
                    </span>
                  </div>
                </div>
              </div>

              <div className="text-xs text-gray-500 space-y-1">
                <div>Date: {formatDate(transaction.created_at)}</div>
                {transaction.details?.transaction_hash && (
                  <div className="truncate">
                    TxHash: {transaction.details.transaction_hash.substring(0, 20)}...
                  </div>
                )}
                {transaction.details?.address && (
                  <div className="truncate">
                    To: {transaction.details.address.substring(0, 20)}...
                  </div>
                )}
                {transaction.details?.fee && (
                  <div>Fee: {formatAmount(transaction.details.fee, transaction.currency)} {transaction.currency?.toUpperCase()}</div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default TransactionHistory;
